import { type ReactElement, useCallback, useMemo } from 'react';

import clsx from 'clsx';
import dayjs from 'dayjs';
import { XAxis } from 'recharts';
import { type AxisDomain, type BaseAxisProps } from 'recharts/types/util/types';

import {
  type DateXAxisType,
  type XAxisType,
  type XProcessedValue,
  type XValue,
} from '@/components/common/charts/types';

import styles from './Axis.module.scss';

export type XLabelProps<XType extends XAxisType> = {
  x: XProcessedValue<XType>;
  index: number;
  values: XValue<XType>[];
};

export type XLabelFormatter<XType extends XAxisType> = (props: XLabelProps<XType>) => string;

export type UseXAxisOptions<XType extends XAxisType> = {
  type: XType;
  rechartsType?: BaseAxisProps['type'];
  values: XValue<XType>[];
  textType?: 'regular14';
  dataKey?: string;
  domain?: AxisDomain;
  formatter?: XLabelFormatter<XType>;
};

const LABEL_DATE_FORMAT: Record<DateXAxisType, string> = {
  date: 'DD.MM.YYYY',
  datetime: 'DD.MM.YYYY HH:mm',
  time: 'HH:mm',
};

const DEFAULT_DOMAIN: AxisDomain = ['dataMin', 'dataMax'];

const isDateAxisType = (type: XAxisType): type is DateXAxisType => {
  return type === 'date' || type === 'datetime' || type === 'time';
};

// хук, а не компонент, потому что recharts не рендерит кастомные обертки их компонентов
export function useXAxis<XType extends XAxisType>(options: UseXAxisOptions<XType>): ReactElement {
  const {
    values,
    type,
    rechartsType = type === 'category' ? 'category' : 'number',
    dataKey = 'x',
    domain = DEFAULT_DOMAIN,
    textType = 'regular14',
    formatter,
  } = options;

  const getXLabel = useCallback(
    (value: XValue<XType>, index: number) => {
      const x = (type === 'number' || type === 'category' ? value : dayjs(value)) as XProcessedValue<XType>;

      return (
        formatter?.({ x, index, values }) ??
        (dayjs.isDayjs(x) && isDateAxisType(type) ? x.format(LABEL_DATE_FORMAT[type]) : String(x))
      );
    },
    [formatter, type, values]
  );

  return useMemo(
    () => (
      <XAxis
        className={clsx(styles.root, styles[`type_${textType}`])}
        type={rechartsType}
        domain={domain}
        dataKey={dataKey}
        tickFormatter={getXLabel}
      />
    ),
    [dataKey, domain, getXLabel, rechartsType, textType]
  );
}
